import React, { useEffect, useRef, useState } from "react";
import "../../../../styles/documents/modals/pdf-modal.css";
import { LinkPicker, LinkOption } from "../../../atoms";
import api from "../../../../utils/api";
import { API_ENDPOINTS } from "../../../../config";
import { DOCUMENT_CATEGORIES } from "../constants";
import { onKanbansRefresh } from "../../../../utils/subscriptionEvents";
import { useIsMobile } from "../../../../hooks/useMediaQuery";

export type PdfDocData = {
  title: string;
  category: string;
  description: string;
  boardLinked?: string;
  jobLinked?: string;
  file?: File;
};

interface Kanban {
  uid: string;
  name: string;
}

interface Job {
  uid: string;
  job_title: string;
  company_name?: string;
}

export interface PdfDocumentModalProps {
  open: boolean;
  onClose: () => void;
  savedValue: PdfDocData;
  onCommit: (v: PdfDocData) => void;
  pdfUrl?: string;
}

const DEFAULT_PDF_URL = "";

export default function PdfDocumentModal({
  open,
  onClose,
  savedValue,
  onCommit,
  pdfUrl = DEFAULT_PDF_URL,
}: PdfDocumentModalProps) {
  const isMobile = useIsMobile();
  useEffect(() => {
  }, [open, pdfUrl]);

  const [title, setTitle] = useState(savedValue.title);
  const [category, setCategory] = useState(savedValue.category);
  const [description, setDescription] = useState(savedValue.description);

  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement | null>(null);

  const [zoom, setZoom] = useState(1);
  const minZ = 0.6;
  const maxZ = 2;
  const stepZ = 0.1;

  const [pdfError, setPdfError] = useState(false);
  const [boards, setBoards] = useState<LinkOption[]>([]);
  const [jobs, setJobs] = useState<LinkOption[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [boardLinked, setBoardLinked] = useState<string | undefined>(savedValue.boardLinked);
  const [jobLinked, setJobLinked] = useState<string | undefined>(savedValue.jobLinked);
  const boardLinkedRef = useRef<string | undefined>(savedValue.boardLinked);

  useEffect(() => {
    if (!open) return;
    setTitle(savedValue.title);
    setCategory(savedValue.category);
    setDescription(savedValue.description);
    setZoom(1);
    setPdfError(false);
    setBoardLinked(savedValue.boardLinked);
    setJobLinked(savedValue.jobLinked);
    boardLinkedRef.current = savedValue.boardLinked;
  }, [open, savedValue]);

  useEffect(() => {
    if (open) {
      fetchBoards();
    }
  }, [open]);

  useEffect(() => {
    const unsubscribe = onKanbansRefresh(() => {
      if (open) {
        fetchBoards();
      }
    });
    return unsubscribe;
  }, [open]);

  useEffect(() => {
    if (boardLinked) {
      fetchJobs(boardLinked);
    } else {
      setJobs([]);
    }
  }, [boardLinked]);

  const fetchBoards = async () => {
    try {
      setLoadingBoards(true);
      const data = await api.get<Kanban[]>(API_ENDPOINTS.kanbans.list);
      const boardOptions: LinkOption[] = data.map(kanban => ({
        id: kanban.uid,
        label: kanban.name
      }));
      setBoards(boardOptions);
      
      const currentBoardLinked = boardLinkedRef.current;
      if (currentBoardLinked) {
        const boardExists = boardOptions.some(b => b.id === currentBoardLinked);
        if (!boardExists) {
          setBoardLinked(undefined);
          boardLinkedRef.current = undefined;
          setJobLinked(undefined);
          setJobs([]);
        }
      }
    } catch (error) {

      setBoards([]);
    } finally {
      setLoadingBoards(false);
    }
  };

  const fetchJobs = async (kanbanUid: string) => {
    try {
      setLoadingJobs(true);
      const data = await api.get<Job[]>(API_ENDPOINTS.jobs.listByKanban(kanbanUid));
      const jobOptions: LinkOption[] = data.map(job => ({
        id: job.uid,
        label: job.company_name ? `${job.job_title} at ${job.company_name}` : job.job_title
      }));
      setJobs(jobOptions);
    } catch (error) {

      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleBoardChange = (boardId: string | null) => {
    const newBoardId = boardId || undefined;
    boardLinkedRef.current = newBoardId;
    setBoardLinked(newBoardId);
    setJobLinked(undefined);
  };

  const handleJobChange = (jobId: string | null) => {
    setJobLinked(jobId || undefined);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (catRef.current && !catRef.current.contains(t)) setCatOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const isDirty =
    title !== savedValue.title ||
    category !== savedValue.category ||
    description !== savedValue.description ||
    boardLinked !== savedValue.boardLinked ||
    jobLinked !== savedValue.jobLinked;

  const handleUpdate = () => {
    if (!isDirty) return;
    onCommit({ title, category, description, boardLinked, jobLinked });
  };

  const zoomOut = () =>
    setZoom((z) => Math.max(minZ, +(z - stepZ).toFixed(2)));
  const zoomIn = () => setZoom((z) => Math.min(maxZ, +(z + stepZ).toFixed(2)));

  if (!open) return null;

  const updateButton = (
    <button
      type="button"
      className={`pdm-btn pdm-btn--primary ${isDirty ? "" : "pdm-btn-disabled"}`}
      onClick={handleUpdate}
      disabled={!isDirty}
    >
      Update
    </button>
  );

  return (
    <div className="pdm-overlay">
      <div className="pdm-overlay-bg" onClick={onClose} />
      <div className="pdm-modal" role="dialog" aria-modal="true">
        <div className="pdm-grid">
          <div className="pdm-left">
            <div className="pdm-toolbar">
              <div className="pdm-toolbar-title">Document</div>

              <div className="pdm-toolbar-actions">
                <div className="pdm-zoom">
                  <button
                    type="button"
                    className="pdm-zoom-btn"
                    aria-label="Zoom out"
                    onClick={zoomOut}
                  >
                    −
                  </button>
                  <button
                    type="button"
                    className="pdm-zoom-btn"
                    aria-label="Zoom in"
                    onClick={zoomIn}
                  >
                    +
                  </button>
                </div>

                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="pdm-btn pdm-btn-download"
                >
                  Download
                </a>
                <button
                  type="button"
                  className="pdm-btn pdm-btn--close"
                  onClick={onClose}
                  aria-label="Close"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="pdm-divider" />

            <div className="pdm-pdfbox">
              {pdfError ? (
                <div className="pdm-pdf-fallback">Preview not available</div>
              ) : (
                <div className="pdm-iframe-wrap">
                  <div
                    className="pdm-iframe-inner"
                    style={{ transform: `scale(${zoom})` }}
                  >
                    <iframe
                      title="pdf"
                      src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                      className="pdm-iframe"
                      onError={() => setPdfError(true)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pdm-right">
            <div className="pdm-right-top">
              <div className="pdm-right-title">Edit Document</div>
              {!isMobile && updateButton}
            </div>

            <div className="pdm-divider" />

            <label className="pdm-label">
              Title <span className="pdm-label-hint">Required</span>
            </label>
            <input
              className="pdm-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <label className="pdm-label">
              Document Category <span className="pdm-label-hint">Required</span>
            </label>
            <div className="pdm-select" ref={catRef}>
              <button
                type="button"
                className="pdm-select-trigger"
                onClick={() => setCatOpen((v) => !v)}
              >
                <span className="pdm-select-value">{category}</span>
                <span className="pdm-select-caret">▾</span>
              </button>

              {catOpen && (
                <div className="pdm-dropdown">
                  {DOCUMENT_CATEGORIES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="pdm-dropdown-item"
                      onClick={() => {
                        setCategory(c);
                        setCatOpen(false);
                      }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <label className="pdm-label">Description</label>
            <textarea
              className="pdm-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ marginBottom: '12px' }}
            />

            <LinkPicker
              label="Boards"
              placeholder={loadingBoards ? "Loading boards..." : "Link board"}
              options={boards}
              value={boardLinked || null}
              onChange={handleBoardChange}
              disabled={loadingBoards}
            />

            <div style={{ marginTop: '12px' }}>
              <LinkPicker
                label="Jobs"
                placeholder={
                  !boardLinked 
                    ? "Select a board first" 
                    : loadingJobs 
                      ? "Loading jobs..." 
                      : "Link job"
                }
                options={jobs}
                value={jobLinked || null}
                onChange={handleJobChange}
                disabled={!boardLinked || loadingJobs}
              />
            </div>

            {isMobile && (
              <div className="pdm-sheet-actions">{updateButton}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}